/**
 * List Component
 * Container for ads within a list, supports drag-and-drop
 */

import React, { useState } from 'react';
import { OrganizationList, OrganizationRule } from '../../types/organization';
import { AdCard } from './AdCard';
import { RuleDisplay } from './RuleDisplay';
import { RuleEditorModal } from './RuleEditorModal';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useUpdateList, useDeleteList, useListAds, useRulesForList, useListsForTab, useMoveAd } from '../../hooks/useOrganization';
import './List.css';

interface ListProps {
  list: OrganizationList;
  tabId: string;
  showYouTubeUploaded: boolean;
  showMetaUploaded: boolean;
  onDelete?: () => void;
}

export const List: React.FC<ListProps> = ({ list, tabId, showYouTubeUploaded, showMetaUploaded, onDelete }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(list.list_name);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showRuleEditor, setShowRuleEditor] = useState(false);
  const [editingRule, setEditingRule] = useState<OrganizationRule | null>(null);
  const [rulesExpanded, setRulesExpanded] = useState(true);
  const [selectedAdIds, setSelectedAdIds] = useState<Set<string>>(new Set());
  const [showMoveToList, setShowMoveToList] = useState(false);
  const [targetListId, setTargetListId] = useState<string>('');

  const { data: allAds = [], isLoading } = useListAds(list.list_id);
  const { data: rules = [] } = useRulesForList(list.is_default ? list.list_id : null);
  const { data: allLists = [] } = useListsForTab(tabId);
  const updateList = useUpdateList();
  const deleteList = useDeleteList();
  const moveAd = useMoveAd();

  const handleEditRule = (rule: OrganizationRule) => {
    setEditingRule(rule);
    setShowRuleEditor(true);
  };

  const handleCloseRuleEditor = () => {
    setShowRuleEditor(false);
    setEditingRule(null);
  };

  const handleToggleSelectAd = (adId: string) => {
    setSelectedAdIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(adId)) {
        newSet.delete(adId);
      } else {
        newSet.add(adId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedAdIds.size === ads.length) {
      setSelectedAdIds(new Set());
    } else {
      setSelectedAdIds(new Set(ads.map(ad => ad.ad_id)));
    }
  };

  const handleCopySelectedIds = async () => {
    const selectedAds = ads.filter(ad => selectedAdIds.has(ad.ad_id));
    const idsText = selectedAds.map(ad => ad.ad_id).join('\n');
    try {
      await navigator.clipboard.writeText(idsText);
      alert(`Copied ${selectedAdIds.size} ad IDs to clipboard`);
    } catch (error) {
      console.error('Failed to copy:', error);
      alert('Failed to copy to clipboard');
    }
  };

  const handleClearSelection = () => {
    setSelectedAdIds(new Set());
  };

  const handleBulkMove = async () => {
    if (!targetListId || selectedAdIds.size === 0) {
      alert('Please select a target list');
      return;
    }

    if (targetListId === list.list_id) {
      alert('Cannot move ads to the same list');
      return;
    }

    const selectedAds = Array.from(selectedAdIds);
    const targetList = allLists.find(l => l.list_id === targetListId);
    
    if (window.confirm(`Move ${selectedAds.length} ads to "${targetList?.list_name}"?`)) {
      try {
        // Move each ad to the target list
        for (const adId of selectedAds) {
          await moveAd.mutateAsync({
            sourceListId: list.list_id,
            adId: adId,
            request: { target_list_id: targetListId, position: null },
          });
        }
        
        // Clear selection and close modal
        setSelectedAdIds(new Set());
        setShowMoveToList(false);
        setTargetListId('');
        
        alert(`Successfully moved ${selectedAds.length} ads`);
      } catch (error) {
        console.error('Bulk move failed:', error);
        alert('Failed to move some ads. Please try again.');
      }
    }
  };

  // Get available lists (excluding current list)
  const availableLists = allLists.filter(l => l.list_id !== list.list_id);

  // Filter ads based on upload status - show/hide ads uploaded to platforms based on toggle
  const ads = allAds.filter(ad => {
    // If both YouTube and Meta are uploaded, show only if both toggles are on
    if (ad.youtube_video_id && ad.meta_ad_id) {
      return showYouTubeUploaded && showMetaUploaded;
    }
    // If only YouTube is uploaded, show only if YouTube toggle is on
    if (ad.youtube_video_id) {
      return showYouTubeUploaded;
    }
    // If only Meta is uploaded, show only if Meta toggle is on
    if (ad.meta_ad_id) {
      return showMetaUploaded;
    }
    // Not uploaded to either platform - always show
    return true;
  });

  const { setNodeRef, isOver } = useDroppable({
    id: list.list_id,
    data: {
      type: 'list',
      listId: list.list_id,
      tabId: list.tab_id,
    },
  });

  const handleNameDoubleClick = () => {
    if (!list.is_default) {
      setIsEditing(true);
    }
  };

  const handleNameSave = () => {
    if (editedName.trim() && editedName !== list.list_name) {
      updateList.mutate({
        listId: list.list_id,
        tabId,
        request: { list_name: editedName.trim() },
      });
    }
    setIsEditing(false);
  };

  const handleNameCancel = () => {
    setEditedName(list.list_name);
    setIsEditing(false);
  };

  const handleDelete = () => {
    if (list.ad_count > 0) {
      setShowDeleteConfirm(true);
    } else {
      performDelete();
    }
  };

  const performDelete = () => {
    deleteList.mutate({ listId: list.list_id, tabId }, {
      onSuccess: () => {
        setShowDeleteConfirm(false);
        onDelete?.();
      },
    });
  };

  const adIds = ads.map((ad) => ad.ad_id);

  return (
    <div className={`list-container ${isOver ? 'drop-target' : ''}`}>
      <div className="list-header">
        {isEditing ? (
          <input
            type="text"
            value={editedName}
            onChange={(e) => setEditedName(e.target.value)}
            onBlur={handleNameSave}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleNameSave();
              if (e.key === 'Escape') handleNameCancel();
            }}
            autoFocus
            className="list-name-input"
          />
        ) : (
          <h3 className="list-name" onDoubleClick={handleNameDoubleClick} title="Double-click to rename">
            {list.list_name}
          </h3>
        )}
        
        <div className="list-header-actions">
          <span className="ad-count">{list.ad_count} ads</span>
          {!list.is_default && (
            <button
              className="delete-list-btn"
              onClick={handleDelete}
              title="Delete list"
              disabled={deleteList.isPending}
            >
              🗑️
            </button>
          )}
        </div>
      </div>

      {/* Rules Section - Only for default (Unsorted) lists */}
      {list.is_default && (
        <div className="rules-section">
          <div className="rules-header" onClick={() => setRulesExpanded(!rulesExpanded)}>
            <h4 className="rules-title">
              📋 Rules ({rules.length})
            </h4>
            <button className="rules-toggle-btn">
              {rulesExpanded ? '▼' : '▶'}
            </button>
          </div>
          
          {rulesExpanded && (
            <div className="rules-content">
              {rules.length === 0 ? (
                <div className="rules-empty">
                  <p>No rules yet</p>
                  <span className="rules-empty-hint">
                    Create rules to automatically import ads to this list
                  </span>
                </div>
              ) : (
                <div className="rules-list">
                  {rules.map((rule) => (
                    <RuleDisplay
                      key={rule.rule_id}
                      rule={rule}
                      onEdit={handleEditRule}
                    />
                  ))}
                </div>
              )}
              
              <button
                className="add-rule-btn"
                onClick={() => setShowRuleEditor(true)}
              >
                + Add Rule
              </button>
            </div>
          )}
        </div>
      )}

      {/* Selection Toolbar */}
      {selectedAdIds.size > 0 && (
        <div className="selection-toolbar">
          <span className="selection-count">{selectedAdIds.size} selected</span>
          <div className="selection-actions">
            <button className="selection-btn" onClick={handleCopySelectedIds} title="Copy ad IDs">
              📋 Copy IDs
            </button>
            <button 
              className="selection-btn move-btn" 
              onClick={() => setShowMoveToList(true)} 
              title="Move to another list"
              disabled={availableLists.length === 0}
            >
              📁 Move to...
            </button>
            <button className="selection-btn" onClick={handleClearSelection} title="Clear selection">
              ✕ Clear
            </button>
          </div>
        </div>
      )}

      <div ref={setNodeRef} className="list-content">
        {isLoading ? (
          <div className="list-loading">Loading...</div>
        ) : ads.length === 0 ? (
          <div className="list-empty">
            <p>Nothing here yet</p>
            <span className="list-empty-hint">Import ads using search above or drag from other lists</span>
          </div>
        ) : (
          <>
            {ads.length > 1 && (
              <div className="select-all-row">
                <input
                  type="checkbox"
                  checked={selectedAdIds.size === ads.length}
                  onChange={handleSelectAll}
                  className="select-all-checkbox"
                />
                <label onClick={handleSelectAll} className="select-all-label">
                  Select All ({ads.length})
                </label>
              </div>
            )}
            <SortableContext items={adIds} strategy={verticalListSortingStrategy}>
              {ads.map((ad) => (
                <AdCard 
                  key={ad.ad_id} 
                  ad={ad} 
                  listId={list.list_id}
                  isSelected={selectedAdIds.has(ad.ad_id)}
                  onToggleSelect={handleToggleSelectAd}
                />
              ))}
            </SortableContext>
          </>
        )}
      </div>

      {showDeleteConfirm && (
        <div className="delete-confirm-modal">
          <div className="delete-confirm-content">
            <h4>Delete List?</h4>
            <p>
              "{list.list_name}" contains {list.ad_count} ads. Are you sure you want to delete it?
              Ads will be moved to Unsorted.
            </p>
            <div className="delete-confirm-actions">
              <button onClick={() => setShowDeleteConfirm(false)} className="btn-secondary">
                Cancel
              </button>
              <button onClick={performDelete} className="btn-danger" disabled={deleteList.isPending}>
                {deleteList.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rule Editor Modal */}
      <RuleEditorModal
        isOpen={showRuleEditor}
        onClose={handleCloseRuleEditor}
        listId={list.list_id}
        existingRule={editingRule}
      />

      {/* Move to List Modal */}
      {showMoveToList && (
        <div className="modal-overlay" onClick={() => setShowMoveToList(false)}>
          <div className="move-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Move {selectedAdIds.size} ads to...</h3>
              <button className="close-btn" onClick={() => setShowMoveToList(false)}>✕</button>
            </div>
            <div className="modal-body">
              {availableLists.length === 0 ? (
                <div className="no-lists-message">
                  <p>No other lists available in this tab.</p>
                  <p>Create a new list to move ads to.</p>
                </div>
              ) : (
                <div className="list-selector">
                  {availableLists.map((targetList) => (
                    <div
                      key={targetList.list_id}
                      className={`list-option ${targetListId === targetList.list_id ? 'selected' : ''}`}
                      onClick={() => setTargetListId(targetList.list_id)}
                    >
                      <input
                        type="radio"
                        name="target-list"
                        checked={targetListId === targetList.list_id}
                        onChange={() => setTargetListId(targetList.list_id)}
                        className="list-radio"
                      />
                      <div className="list-option-info">
                        <span className="list-option-name">{targetList.list_name}</span>
                        <span className="list-option-count">{targetList.ad_count} ads</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button 
                className="btn-secondary" 
                onClick={() => {
                  setShowMoveToList(false);
                  setTargetListId('');
                }}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleBulkMove}
                disabled={!targetListId || moveAd.isPending}
              >
                {moveAd.isPending ? 'Moving...' : 'Move Ads'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

